import moment from "moment";
import currency from "currency.js";
import { LABON_THANKSGIVING_EXCLUSION_LIST } from "../constants";

export function getQueryParam(queryStr, p) {
  const arr = queryStr.replace("?", "").split("&");
  for (const a of arr) {
    if (a.startsWith(`${p}=`)) {
      return decodeURIComponent(a.substring(p.length + 1));
    }
  }
  return undefined;
}

export function capitalize(s) {
  const words = s.split(" ");
  for (let i = 0; i < words.length; i++) {
    if (words[i]) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
  }
  return words.join(" ");
}

export function createSnippets(productType, shape, size, servings, eventDate) {
  const data = [];
  if (productType === "Cake") {
    if (shape && size) {
      data.push(`${shape}, ${size} cake`);
    }
    if (servings) {
      data.push(`${servings} servings`);
    }
    data.push(`Choice of flavor`);
    data.push(`Custom inscription`);
  } else {
    if (productType && servings) {
      data.push(`${servings} ${productType}`);
    }
    if (eventDate) {
      data.push(`For ${moment(eventDate).format("ddd, MMM DD, YYYY")}`);
    }
  }

  return data;
}

export function generateServingsSnippet(servings) {
  if (servings.length < 7) {
    return servings + " servings";
  } else {
    return servings;
  }
}

export function generateTitle(items, keyword) {
  let title = "";

  if (Object.keys(items).length === 1) {
    // single item
    title = `${keyword || "Custom"} ${Object.keys(items)[0]}`;
  } else {
    // multiple items
    if (items["Cake"]) {
      title += `${keyword || "Custom"} Cake & Treats`;
    } else {
      title += `Custom Treats`;
    }
  }

  return capitalize(title);
}

export function generateProductSnippets(productType, shape, size, servings) {
  const data = [];
  if (productType === "Cake") {
    if (shape && size) {
      data.push(`${shape}, ${size} cake`);
    }
    if (servings) {
      data.push(`${servings} servings`);
    }
  } else {
    if (productType && servings) {
      data.push(`${servings} ${productType}`);
    }
  }

  return data;
}

export function generateSizeSummary(variant) {
  let arr = [];

  if (variant) {
    if (variant.name) {
      arr.push(variant.name);
    } else {
      if (variant.shape) {
        arr.push(variant.shape);
      }
      if (variant.size) {
        arr.push(variant.size);
      }
      if (variant.servings) {
        arr.push(`${variant.servings} servings`);
      }
    }
  }

  return arr.join(" • ");
}

export function generateFlavorSummary(arr) {
  let summary = "";
  if (Array.isArray(arr)) {
    for (const o of arr) {
      if (o.name) {
        summary = `Flavor: ${o.name}`;
      }

      if (o.filling) {
        summary += ` • Filling: ${o.filling}`;
      }
    }
  } else if (typeof arr === "object") {
    if (arr.base) {
      summary = `Flavor: ${arr.base.name}`;
    }
    if (arr.filling) {
      summary += ` • Filling: ${arr.filling.name}`;
    }
  }

  return summary;
}

export function getCustomerName(customer) {
  if (customer.firstName && customer.lastName) {
    return `${customer.firstName} ${customer.lastName}`;
  } else if (customer.name && customer.lastName) {
    return `${customer.name} ${customer.lastName}`;
  } else {
    return customer.name || "";
  }
}

export function getAddress(location) {
  let address = "";
  if (location.street) {
    address = `${location.street}, `;
  }

  address += `${location.city}, ${location.state} ${location.zip}`;
  return address;
}

export function getDaysWithNoTimeslots(timeslots) {
  let days = [];
  for (const [key, item] of Object.entries(timeslots)) {
    if (!item || item.length === 0) {
      days.push(key.toLowerCase());
    }
  }
  return days;
}

export function getDateRules(items) {
  let rules = {};

  for (const item of items) {
    if (item.rules && item.rules.date) {
      if (item.rules.date.leadTime) {
        rules.leadTime = Math.max(rules.leadTime || 0, item.rules.date.leadTime);
      }
      if (item.rules.date.daysToExclude) {
        if (rules.daysToExclude) {
          rules.daysToExclude = [...new Set(rules.daysToExclude.concat(...item.rules.date.daysToExclude))];
        } else {
          rules.daysToExclude = item.rules.date.daysToExclude;
        }
      }
      if (item.rules.date.min) {
        if (rules.min) {
          rules.min = moment(rules.min).isBefore(moment(item.rules.date.min)) ? item.rules.date.min : rules.min;
        } else {
          rules.min = item.rules.date.min;
        }
      }

      if (item.rules.date.max) {
        if (rules.max) {
          rules.max = moment(rules.max).isAfter(moment(item.rules.date.max)) ? item.rules.date.max : rules.max;
        } else {
          rules.max = item.rules.date.max;
        }
      }
    }
  }
  return rules;
}

export function shouldDisableDate(siteId, date, items, fulfillment) {
  const dateRules = getDateRules(items);

  if (dateRules) {
    if (dateRules.min) {
      if (moment(date).isBefore(moment(dateRules.min))) {
        return true;
      }
    }

    if (dateRules.max) {
      if (moment(date).isAfter(moment(dateRules.max))) {
        return true;
      }
    }

    if (dateRules.daysToExclude) {
      const day = moment(date).format("ddd").toLowerCase();
      if (dateRules.daysToExclude.includes(day)) {
        return true;
      }
    }

    if (dateRules.leadTime) {
      // if leadTime extends through end of day, then disable date
      const hoursToEndOfDay = moment(date).endOf("day").diff(moment(), "hours") + getTimezoneOffset();
      if (+hoursToEndOfDay < +dateRules.leadTime) {
        return true;
      }
    }
  }

  // Metrobi exclusion
  if (fulfillment && fulfillment.shipping === "delivery" && fulfillment.provider?.name === "metrobi") {
    // 24-hour lead time
    const hoursToStartOfDay = moment(date).startOf("day").diff(moment(), "hours") + getTimezoneOffset();
    if (+hoursToStartOfDay < 10) {
      return true;
    }
  }

  // date exclusion
  if (fulfillment.location.rules?.date?.block.includes(moment(date).format("YYYY-MM-DD"))) {
    return true;
  } else {
    // check if date is specifically enabled
    const timeslots = getAvailableTimeslotsbyDate(siteId, fulfillment.location, moment(date).format("YYYY-MM-DD"), dateRules);
    if (!timeslots || timeslots.length === 0) {
      return true;
    }
  }

  return false;
}

export function checkItemAvailabilityByDate(siteId, items, date, storeLocation) {
  const dateRules = getDateRules(items);

  if (dateRules) {
    if (dateRules.min) {
      if (moment(date).isBefore(moment(dateRules.min))) {
        return false;
      }
    }

    if (dateRules.max) {
      if (moment(date).isAfter(moment(dateRules.max))) {
        return false;
      }
    }

    if (dateRules.daysToExclude) {
      const day = moment(date).format("ddd").toLowerCase();
      if (dateRules.daysToExclude.includes(day)) {
        return false;
      }
    }

    if (dateRules.leadTime) {
      // if leadTime extends through end of day, then disable date
      const hoursToEndOfDay = moment(date).endOf("day").diff(moment(), "hours") + getTimezoneOffset();
      if (+hoursToEndOfDay < +dateRules.leadTime) {
        return false;
      }

      const timeslots = getAvailableTimeslotsbyDate(siteId, storeLocation?.fulfillment?.pickup, moment(date).format("YYYY-MM-DD"), dateRules);
      if (!timeslots || timeslots.length === 0) {
        return false;
      }
    }
  }

  // check inventory
  for (const item of items) {
    if (item.inventory && item.inventory[date]) {
      const inventoryByDate = item.inventory[date];
      if (inventoryByDate && !(+inventoryByDate.threshold - +inventoryByDate.sold > 0)) {
        return false;
      }
    }

    // TBH-specific
    if (siteId === "99i69dd8bwTqlxzVLZUx") {
      if (["2025-11-25", "2025-11-26"].includes(moment(date).format("YYYY-MM-DD"))) {
        if (
          !(
            arrayContainsIgnoreCase(item.category, "Thanksgiving") ||
            arrayContainsIgnoreCase(item.category, "Speciality Cakes") ||
            arrayContainsIgnoreCase(item.category, "Sports Cake")
          )
        ) {
          return false;
        }
      } else if (["2025-12-23", "2025-12-24"].includes(moment(date).format("YYYY-MM-DD"))) {
        if (
          !(
            arrayContainsIgnoreCase(item.category, "Thanksgiving") ||
            arrayContainsIgnoreCase(item.category, "Speciality Cakes") ||
            arrayContainsIgnoreCase(item.category, "Sports Cake")
          )
        ) {
          return false;
        }
      }
    } else if (siteId === "ZOsiHlROlfEcAO7PGGCK") {
      // Labon specific
      if (["2025-11-26", "2025-11-27"].includes(moment(date).format("YYYY-MM-DD"))) {
        if (arrayContainsIgnoreCase(item.category, "King Cakes") || LABON_THANKSGIVING_EXCLUSION_LIST.includes(item.id)) {
          return false;
        }
      }
    }
  }

  return true;
}

export function checkStoreAvailabilityByDate(siteId, storeLocation, date) {
  if (storeLocation.rules?.date?.block.includes(moment(date).format("YYYY-MM-DD"))) {
    return false;
  } else {
    const timeslots = getAvailableTimeslotsbyDate(siteId, storeLocation?.fulfillment?.pickup, moment(date).format("YYYY-MM-DD"));
    if (!timeslots || timeslots.length === 0) {
      return false;
    }
  }

  return true;
}

export function getStoreClosedDaysOfWeek(storeLocation) {
  const DAY_MAP = {
    sun: 1,
    mon: 2,
    tue: 3,
    wed: 4,
    thu: 5,
    fri: 6,
    sat: 7
  };

  let arr = [];
  for (const [key, value] of Object.entries(storeLocation.fulfillment?.pickup?.timeslots)) {
    if (value?.length === 0) {
      arr.push(DAY_MAP[key]);
    }
  }

  return arr.sort();
}

export function getItemBasePrice(variant) {
  for (const [key, value] of Object.entries(variant)) {
    if (key === "size") {
      return +value.pricing.amount;
    }
  }

  return 0;
}

export function getVariantTotal(variant, basePrice) {
  let total = 0;
  if (variant) {
    for (const [key, value] of Object.entries(variant)) {
      if (value?.pricing) {
        if (value.pricing.amount) {
          total += +value.pricing.amount;
        } else if (value.pricing.factor) {
          total += currency(basePrice).multiply(value.pricing.factor).value;
        }
      } else if (typeof value === "object") {
        total += getVariantTotal(value, basePrice);
      }
    }
  }
  return +total;
}

export function getAvailableTimeslotsbyDate(siteId, location, date, dateRules) {
  let slotsbyDate = location?.timeslots[moment(date).format("ddd").toLowerCase()];
  let today = moment();

  // special early-closing
  if (siteId === "RaKfruZdNNMhNqd0ZF65" && date === "2025-12-24") {
    slotsbyDate = ["10am - 11am", "11am - Noon"];
  } else if (siteId === "RaKfruZdNNMhNqd0ZF65" && date === "2025-11-15") {
    slotsbyDate = ["5pm - 6pm"];
  } else if (siteId === "RaKfruZdNNMhNqd0ZF65" && date === "2025-11-27") {
    slotsbyDate = ["10am - 11am", "11am - Noon"];
  } else if (siteId === "RaKfruZdNNMhNqd0ZF65" && date === "2025-11-28") {
    slotsbyDate = ["2pm - 3pm", "3pm - 4pm", "4pm - 5pm", "5pm - 6pm"];
  } else if (siteId === "6WOVOXtelMJ1ZwfEWs17" && date === "2025-10-31") {
    slotsbyDate = ["8am - 9am", "9am - 10am", "10am - 11am", "11am - Noon", "Noon - 1pm", "1pm - 2pm", "2pm - 3pm"];
  } else if (siteId === "6WOVOXtelMJ1ZwfEWs17" && date === "2025-11-27") {
    slotsbyDate = ["8am - 9am", "9am - 10am", "10am - 11am", "11am - Noon", "Noon - 1pm", "1pm - 2pm", "2pm - 3pm"];
  } else if (siteId === "JptQx8l2IeirCf5CW8d0" && date === "2025-11-27") {
    slotsbyDate = ["11am - Noon", "Noon - 1pm", "1pm - 2pm"];
  } else if (siteId === "Z3bGmikh3cWqw3HYKGIA" && date === "2025-11-27") {
    slotsbyDate = ["9am - 10am", "10am - 11am", "11am - Noon", "Noon - 1pm", "1pm - 1.30pm"];
  } else if (siteId === "sSeHPbi9QHiaKp23TBYF" && date === "2025-11-14") {
    slotsbyDate = [
      "7am - 8am",
      "8am - 9am",
      "9am - 10am",
      "10am - 11am",
      "11am - Noon",
      "Noon - 1pm",
      "1pm - 2pm",
      "2pm - 3pm",
      "3pm - 4pm",
      "4pm - 5pm",
      "5pm - 6pm"
    ];
  } else if (siteId === "sSeHPbi9QHiaKp23TBYF" && date === "2025-11-27") {
    slotsbyDate = ["9am - 10am", "10am - 11am", "11am - Noon"];
  } else if (siteId === "ZOsiHlROlfEcAO7PGGCK" && date === "2025-11-27") {
    slotsbyDate = ["8am - 9am", "9am - 10am", "10am - 11am", "11am - Noon", "Noon - 1pm", "1pm - 2pm"];
  }

  // check today's timeslots
  if (moment(date).format("YYYY-MM-DD") === today.format("YYYY-MM-DD")) {
    console.log("TODAY");

    const currentHour = today.hour() + getTimezoneOffset();
    console.log("currentHour", currentHour, today.hour());

    let arr = [];
    for (const o of slotsbyDate) {
      const hour = getStartTime(o);
      if (+hour > currentHour) {
        arr.push(o);
      }
    }
    slotsbyDate = arr;
  }

  // check leadTime
  if (slotsbyDate && dateRules?.leadTime) {
    // do we even need to check time?
    const hoursToStartOfDay = Math.ceil(moment(date).startOf("day").diff(moment(), "hours", true)) + getTimezoneOffset();
    if (+dateRules.leadTime > hoursToStartOfDay) {
      let arr = [];
      for (const o of slotsbyDate) {
        const hourOfDay = getStartTime(o);
        const durationToTimeslot = hoursToStartOfDay + hourOfDay;
        if (durationToTimeslot >= +dateRules.leadTime) {
          arr.push(o);
        }
      }
      slotsbyDate = arr;
    }
  }

  return slotsbyDate;
}

export function getStartTime(str) {
  let hour = 0;
  const time = (str?.trim() || "").toLowerCase();

  const amIdx = time.indexOf("am");
  const noonIdx = time.indexOf("noon");
  const pmIdx = time.indexOf("pm");

  // find first occurence
  const arr = [amIdx, noonIdx, pmIdx].filter((idx) => idx >= 0);
  if (arr.length > 0) {
    // split time string at the first match
    const splitIdx = Math.min(...arr);
    if (noonIdx === splitIdx) {
      hour = 12;
    } else {
      // split string and extract hour
      hour = parseInt(time.substring(0, splitIdx));
      if (pmIdx === splitIdx) {
        if (hour !== 12) {
          hour = hour + 12;
        }
      }
    }
  }

  return hour;
}

export function getTimezoneOffset() {
  const storeLocationTimezoneOffset = 4;
  const clientTimezoneOffset = Math.floor(new Date().getTimezoneOffset() / 60);

  const offset = clientTimezoneOffset - storeLocationTimezoneOffset;

  return offset;
}

export const formatPhoneNumber = (value) => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) return value;

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, "");

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  var match = phoneNumber.match(/^(\d{1,3}|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] ? `+${match[1]} ` : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  } else {
    return value;
  }
};

export function getCustomerFirstNameLastInitial(name) {
  // Create an array containing each of the words in the name
  var names = name.split(/\s+/);

  if (names.length > 1) {
    // Replaces the first name with an initial, followed by a period.
    names[1] = names[1].substr(0, 1) + ".";
  }

  // Glue the pieces back together.
  return names.join(" ");
}

export function isAssistedCheckout() {
  const isPageInIFrame = window.location !== window.parent.location;
  const isAssistedCheckout = sessionStorage.getItem("su");
  return isPageInIFrame && isAssistedCheckout;
}

export function getItemMetadata(product) {
  let [minPrice, maxPrice, leadTime] = [0, 0, 0];
  let dateRange = "";
  let daysOfWeek = "";

  let dietary = new Set();
  if (product.variants?.size && product.variants?.size?.field?.options) {
    for (const variant of Object.values(product.variants.size.field.options)) {
      const title = variant.name.toLowerCase();
      if (title.includes("vegan")) {
        dietary.add("VG");
      }

      if (title.includes("gluten") && title.includes("free")) {
        dietary.add("GF");
      }

      const price = +variant.pricing.amount;
      if (minPrice === 0) {
        minPrice = price;
      } else if (price <= minPrice) {
        minPrice = price;
      } else if (price >= maxPrice) {
        maxPrice = price;
      }
    }
  }

  leadTime = product.rules?.date?.leadTime || 0;
  if (product.rules?.date) {
    if (product.rules.date?.min && product.rules?.date?.max) {
      dateRange = `Available ${moment(product.rules.date.min).format("MMM D")} - ${moment(product.rules?.date?.max).format("MMM D")}`;
    } else if (product.rules.date?.min) {
      dateRange = `Available ${moment(product.rules?.date?.min).format("MMM D")} onwards`;
    } else if (product.rules.date?.max) {
      dateRange = `Available until ${moment(product.rules?.date?.max).format("MMM D")}`;
    }

    if (product.rules.date.daysToExclude) {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].filter(
        (item) => !product.rules.date.daysToExclude.join(",").toLowerCase().includes(item.toLowerCase())
      );
      if (days.length < 7) {
        daysOfWeek = `Available ${days.join(", ")} only`;
      }
    }
  }

  return { price: { min: minPrice, max: maxPrice }, dietary: Array.from(dietary), leadTime, dateRange, daysOfWeek };
}

export function getBookingMetadata(product) {
  // inspect rules
  let leadTime = product.rules?.date?.leadTime || 0;
  let capacity = product.rules?.space?.capacity || 0;
  let duration = product.rules?.duration?.min || 0;
  let area = product.rules?.space?.area || 0;

  return { leadTime, capacity, area, duration };
}

export function getStoreAddress(store) {
  return `${store.address.street}, ${store.address.city}, ${store.address.state} ${store.address.zip}`;
}

export function getStaticMapUrl(stores) {
  const mainLocation = stores[0];
  let url = `https://maps.googleapis.com/maps/api/staticmap?scale=2`;

  // now add markers
  stores.forEach((store, index) => {
    let markerConfig = "";
    if (stores.length > 1) {
      markerConfig = `label:${index + 1}|`;
    }
    markerConfig += getStoreAddress(store);

    url += `&markers=${encodeURIComponent(markerConfig)}`;
  });

  url += `&maptype=roadmap&size=640x320&key=AIzaSyALpb-XVmhGjST5IRtRW-yGcbsIqLc7gJs`;

  console.log("return", url);
  return url;
}

export function isTruncated(element) {
  if (element && (element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)) {
    return true;
  } else {
    return false;
  }
}

export function normalizeFlavor(value) {
  if (Array.isArray(value)) {
    return value[0];
  } else if (typeof value === "object" && value.name) {
    // v2.0 format
    return value;
  } else if (typeof value === "object") {
    return { name: value?.base?.name, filling: value?.filling?.name };
  } else {
    return "";
  }
}

export function generateItemSnippets(item) {
  const data = [];

  if (item.variant) {
    if (item.variant.size) {
      const size = generateSizeSummary(item.variant.size);
      if (size) {
        data.push(`Size • ${size}`);
      }
    }

    /*if (item.variant.frosting) {
      data.push(`Icing • ${item.variant.frosting.name}`);
    }*/
    if (item.variant.flavor) {
      const flavor = normalizeFlavor(item.variant.flavor);
      if (flavor.name) {
        data.push(`Flavor • ${flavor.name}`);
      }

      if (flavor.filling) {
        data.push(`Filling • ${flavor.filling}`);
      }
    }
    for (const [key, value] of Object.entries(item.variant)) {
      if (["size", "flavor", "add-on", "session", "photo"].includes(key)) {
        continue;
      } else if (value?.name) {
        data.push(`${capitalize(key.replaceAll("-", " "))} • ${value?.name}`);
      }
    }
    if (item.variant?.[`add-on`]) {
      if (Array.isArray(item.variant?.[`add-on`])) {
        // old format: Delete
        const addOnValues = item.variant?.[`add-on`].map((o) => o.name).join(", ");
        data.push(`Add-Ons • ${addOnValues}`);
      } else {
        // New format
        data.push(`Add-Ons • ${item.variant?.[`add-on`]?.name}`);
      }
    }
    if (item.variant.session) {
      data.push(`Session • ${moment(item.variant.session.date, "YYYY-MM-DD").format("MMM D, YYYY")} ${item.variant.session.time}`);
    }
  }

  if (item.customization) {
    if (item.customization.inscription && item.customization.inscription.text) {
      data.push(`Inscription: ${item.customization.inscription.text}`);
    }
  }

  if (item.tags) {
    data.push(...item.tags);
  }

  return data;
}

export function getFormattedAddressAsArray(location, includeUnit) {
  let arr = [];

  if (location) {
    let line1 = location.street;
    if (includeUnit && location.unit) {
      line1 += `, ${location.unit}`;
    }
    arr.push(line1);
    arr.push(`${location.city}, ${location.state} ${location.zip}`);
  }
  return arr;
}

export function slugify(str) {
  str = str.replace(/^\s+|\s+$/g, ""); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, "") // remove any non-alphanumeric characters
    .replace(/\s+/g, "-") // replace spaces with hyphens
    .replace(/-+/g, "-"); // remove consecutive hyphens
  return str;
}

export function getItemUrl(item) {
  const category = encodeURIComponent(slugify(item.category?.length > 0 ? item.category[0] : item.type));
  const slug = encodeURIComponent(slugify(`${item.title}`));
  return `/shop/${category}/${slug}-${item.id}`;
}

export function isEmailValid(email) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
}

export function anyWordStartsWith(string, prefix) {
  string = string.toLowerCase();
  prefix = prefix.toLowerCase();

  if (string.startsWith(prefix)) {
    return true;
  }

  let tokens = string.toLowerCase().split(" ");
  for (const token of tokens) {
    if (token.trim().startsWith(prefix)) {
      return true;
    }
  }

  return false;
}

export function getVariantOptionSurchargeForDisplay(option, basePrice) {
  let surcharge = "";
  if (+option.pricing?.amount > 0) {
    surcharge = currency(option.pricing?.amount).format();
  } else if (+basePrice > 0 && +option.pricing?.factor > 0) {
    surcharge = `${currency(basePrice).multiply(option.pricing?.factor).format()}`;
  } else if (+option.pricing?.factor > 0) {
    surcharge = `${(option.pricing.factor * 100).toFixed(2)}%`;
  }

  return surcharge;
}

export function convertOptions(name, options, rank) {
  let obj = {
    name,
    description: "",
    rank,
    required: true,
    field: {
      type: "select",
      min: 1,
      max: 1
    }
  };
  obj.field.options = options.map((o) => {
    return {
      name: o.name,
      description: o.desc,
      pricing: {
        type: "fixed",
        amount: o.fee
      }
    };
  });

  return obj;
}

export function calculatePlatformFee(amount, platformFeePlan) {
  let platformFee = currency(0);
  if (platformFeePlan) {
    platformFee = currency(Math.max(currency(amount).multiply(platformFeePlan.factor).value, +platformFeePlan.floor));
  }

  return platformFee;
}

export function getDataFromLocalStorage(key, ttl) {
  const value = localStorage.getItem(key);

  if (value) {
    // parse to JSON
    const data = JSON.parse(value);
    if (ttl && data.ts) {
      const cacheAge = Date.now() - +data.ts;
      if (cacheAge > +ttl) {
        localStorage.removeItem(key);
        return "";
      }
    }

    return data;
  }

  return "";
}

export function setDataInLocalStorage(key, object) {
  localStorage.setItem(key, JSON.stringify({ ...object, ts: Date.now() }));
}

export function getOrdinalSuffix(number) {
  if (typeof number !== "number" || !Number.isInteger(number)) {
    // Or throw an error: throw new Error("Input must be an integer.");
    return String(number); // Handle non-integer or non-numeric input gracefully
  }

  const lastTwoDigits = number % 100;
  const lastDigit = number % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return number + "th";
  }

  switch (lastDigit) {
    case 1:
      return number + "st";
    case 2:
      return number + "nd";
    case 3:
      return number + "rd";
    default:
      return number + "th";
  }
}

export function sortBySurcharge(o1, o2) {
  const amt1 = o1.pricing?.amount || o1.pricing?.factor || 0;
  const amt2 = o2.pricing?.amount || o2.pricing?.factor || 0;
  let rslt = +amt1 - +amt2;
  if (rslt === 0) {
    rslt = o1.name.localeCompare(o2.name);
  }

  return rslt;
}

export function getItemSurchargeTotal(variant, basePrice) {
  let total = 0;
  if (variant) {
    for (const [key, value] of Object.entries(variant)) {
      if (key === "size") {
        continue;
      }

      console.log("value", key, value);
      total += getVariantTotal(value, basePrice);
    }
  }
  return +total;
}

export function areTipsEnabled(siteId) {
  return !(siteId === "99i69dd8bwTqlxzVLZUx" || siteId === "ZOsiHlROlfEcAO7PGGCK");
}

export function getOptionListByOverride(options, overrides) {
  let arr = [];
  for (const option of options) {
    let keep = true;
    if (overrides && option.override) {
      for (const [key, value] of Object.entries(overrides)) {
        // does the option have an override for this key?
        if (option.override[key] && option.override[key][value]) {
          if (String(option.override[key][value].allow) === "false") {
            keep = false;
            continue;
          } else if (option.override[key][value].amount) {
            option.pricing = { type: "fixed", amount: option.override[key][value].amount };
          }
        }
      }
    }
    if (keep) {
      arr.push(option);
    }
  }
  return arr;
}

export function extractSizeRank(str) {
  // Extract the first number preceding the words "inch", '"' or "sheet"
  if (!str) return NaN;

  str = str.toLowerCase();
  if (str.includes("sheet")) {
    if (str.includes("quarter") || str.includes("1/4")) {
      return 100;
    } else if (str.includes("half") || str.includes("1/2")) {
      return 101;
    } else if (str.includes("full")) {
      return 102;
    }
  }

  // Improved regex: look for a number (integer or decimal) followed by optional space, optional dash, and then ("inch" or '"' or "sheet")
  // Handles cases like "14-inch", "14 inch", '14"'
  // Also matches numbers before "tier" as a fallback if no inch found
  let match = str.match(/(\d+(\.\d+)?)(?=\s*-?\s*(inch|\"|sheet))/i);

  if (!match) {
    // fallback: look for number before "tier"
    match = str.match(/(\d+(\.\d+)?)(?=\s*-?\s*tier)/i);
  }
  return match ? parseFloat(match[1]) : NaN;
}

export function sortVariantOptions(fieldKey, o1, o2) {
  if (fieldKey === "size") {
    let n1 = extractSizeRank(o1.name);
    let n2 = extractSizeRank(o2.name);

    if (!isNaN(n1) && !isNaN(n2)) {
      return n1 - n2;
    } else if (o1.pricing?.amount && o2.pricing?.amount) {
      return o1.pricing.amount - o2.pricing.amount;
    } else {
      return o1?.name?.localeCompare(o2?.name) || 0;
    }
  } else {
    return o1?.name?.localeCompare(o2?.name) || 0;
  }
}

export function equalsIgnoreCase(str1, str2) {
  if (str1 && str2) {
    return str1.trim().toLowerCase() === str2.trim().toLowerCase();
  }
  return false;
}

export function arrayContainsIgnoreCase(arr, str) {
  if (arr && str) {
    return arr.filter((o) => o.trim().toLowerCase() === str.trim().toLowerCase()).length > 0;
  }
  return false;
}
